import React from 'react';
import { X, Calendar, User, ClipboardList, ArrowRight, RefreshCcw } from 'lucide-react';
// @ts-ignore
import { Action, ActionHealthStatus } from '../types';

interface ActionSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  actions: Action[];
  cardTitle: string;
  onOpenInMemo: (cardId: string, actionId?: string) => void;
  cardId: string;
}

// Calcular status de saúde da ação (baseado no prazo)
const getActionHealth = (action: Action): ActionHealthStatus => {
  // Se a ação está concluída, sempre verde
  if (action.status === 'done') return 'green';
  
  // Se não tem prazo, considerar verde (sem urgência)
  if (!action.deadline) return 'green';
  
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const deadline = new Date(action.deadline);
    deadline.setHours(0, 0, 0, 0);
    
    const diffDays = Math.floor((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    // Vermelho: atrasada (deadline < hoje)
    if (diffDays < 0) return 'red';
    
    // Amarelo: próximo do prazo (0 a 3 dias)
    if (diffDays <= 3) return 'yellow';
    
    // Verde: dentro do prazo (> 3 dias)
    return 'green';
  } catch {
    return 'green';
  }
};

// Obter label do status de saúde
const getHealthLabel = (health: ActionHealthStatus): string => {
  switch (health) {
    case 'green': return 'No Prazo';
    case 'yellow': return 'Próximo do Prazo';
    case 'red': return 'Atrasada';
    default: return 'Sem Prazo';
  }
};

// Obter cor do badge de saúde
const getHealthBadgeColor = (health: ActionHealthStatus): string => {
  switch (health) {
    case 'green': return 'bg-green-500';
    case 'yellow': return 'bg-yellow-500';
    case 'red': return 'bg-red-500';
    default: return 'bg-gray-500';
  }
};

// Obter cor do texto do status de saúde
const getHealthTextColor = (health: ActionHealthStatus): string => {
  switch (health) {
    case 'green': return 'text-green-700';
    case 'yellow': return 'text-yellow-700';
    case 'red': return 'text-red-700';
    default: return 'text-gray-700';
  }
};

// Obter label do status da ação
const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'todo': return 'A Fazer';
    case 'doing': return 'Em Andamento';
    case 'done': return 'Concluída';
    case 'canceled': return 'Cancelada';
    default: return status;
  }
};

// Obter cor do status da ação
const getStatusColor = (status: string): string => {
  switch (status) {
    case 'todo': return 'bg-gray-100 text-gray-700 border-gray-300';
    case 'doing': return 'bg-blue-100 text-blue-700 border-blue-300';
    case 'done': return 'bg-green-100 text-green-700 border-green-300';
    case 'canceled': return 'bg-gray-50 text-gray-400 border-gray-200';
    default: return 'bg-gray-100 text-gray-700';
  }
};

export const ActionSummaryModal: React.FC<ActionSummaryModalProps> = ({
  isOpen,
  onClose,
  actions,
  cardTitle,
  onOpenInMemo,
  cardId,
}) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded flex items-center justify-center">
              <ClipboardList className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Ações Vinculadas</h2>
              <p className="text-sm text-blue-100">{cardTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded transition-colors"
            title="Fechar"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {actions.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-600 font-medium">Nenhuma ação vinculada a este card</p>
              <p className="text-sm text-gray-500 mt-2">Crie ações em Ações e vincule-as a este card</p>
            </div>
          ) : (
            <div className="space-y-4">
              {actions.map((action) => {
                const health = getActionHealth(action);
                const deadline = action.deadline ? new Date(action.deadline) : null;
                
                return (
                  <div
                    key={action.id}
                    className={`border-2 rounded p-4 transition-all hover:shadow-md ${
                      health === 'red' ? 'border-red-200 bg-red-50' :
                      health === 'yellow' ? 'border-yellow-200 bg-yellow-50' :
                      'border-gray-200 bg-white'
                    }`}
                  >
                    {/* Header da Ação */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-base mb-1">
                          {action.title}
                        </h3>
                        {action.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {action.description}
                          </p>
                        )}
                      </div>
                      <span className={`ml-3 px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(action.status)}`}>
                        {getStatusLabel(action.status)}
                      </span>
                    </div>

                    {/* Informações Resumidas */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      {/* Prazo */}
                      <div className="flex items-center gap-2">
                        <Calendar 
                          size={16} 
                          className={`flex-shrink-0 ${
                            health === 'red' ? 'text-red-600' :
                            health === 'yellow' ? 'text-yellow-600' :
                            'text-gray-600'
                          }`} 
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-500">Prazo</p>
                            {action.deadlineHistory && action.deadlineHistory.length > 0 && (
                              <span 
                                className="flex items-center gap-0.5 text-amber-600 font-bold bg-amber-50 px-1 rounded border border-amber-100 text-[9px]"
                                title={`${action.deadlineHistory.length} renegociações de prazo`}
                              >
                                <RefreshCcw size={8} />
                                {action.deadlineHistory.length}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {action.originalDeadline && action.originalDeadline !== action.deadline && (
                              <span 
                                className="line-through text-gray-400 text-xs opacity-70"
                                title={`Prazo Original: ${new Date(action.originalDeadline).toLocaleDateString('pt-BR')}${action.deadlineHistory?.length ? `\nÚltimo Motivo: ${action.deadlineHistory[action.deadlineHistory.length - 1].reason}` : ''}`}
                              >
                                {new Date(action.originalDeadline).toLocaleDateString('pt-BR')}
                              </span>
                            )}
                            <p className={`text-sm font-medium ${
                              health === 'red' ? 'text-red-700 font-bold' :
                              health === 'yellow' ? 'text-yellow-700' :
                              'text-gray-900'
                            }`}>
                              {deadline ? deadline.toLocaleDateString('pt-BR') : 'Não definido'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Responsável */}
                      <div className="flex items-center gap-2">
                        <User size={16} className="flex-shrink-0 text-gray-600" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500">Responsável</p>
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {action.responsible || 'Não definido'}
                          </p>
                        </div>
                      </div>

                      {/* Saúde */}
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full flex-shrink-0 ${getHealthBadgeColor(health)}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500">Saúde</p>
                          <p className={`text-sm font-semibold ${getHealthTextColor(health)}`}>
                            {getHealthLabel(health)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Botão Abrir no MEMO */}
                    <button
                      onClick={() => {
                        onOpenInMemo(cardId, action.id);
                        onClose();
                      }}
                      className="w-full mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded transition-colors flex items-center justify-center gap-2"
                    >
                      <ArrowRight size={16} />
                      Abrir em Ações
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {actions.length > 0 && (
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <button
              onClick={() => {
                onOpenInMemo(cardId);
                onClose();
              }}
              className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded transition-colors flex items-center justify-center gap-2"
            >
              <ClipboardList size={18} />
              Abrir Todas as Ações em Ações
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

